// 위생 클레임 데모. 요구사항 REQ-QSC-I-001 기준 구조이며 매장·제품·내용은 예시입니다.
// 유형·입력항목·등급 기준은 QSC팀·CS팀 확정 전 가안이다. 확정되면 아래 상수만 바꾼다.
import dayjs from 'dayjs'
import type { TagTone } from '@/shared/ui'

/** 클레임 구분. 원·부자재 이물은 엠즈푸드 등록 건(연동 보류)과 같은 구분이다. */
export type ClaimKind = '매장 이물' | '원·부자재 이물'
export const CLAIM_KINDS: ClaimKind[] = ['매장 이물', '원·부자재 이물']

// ponytail: 가안 유형. QSC팀·CS팀 공동 정리안이 오면 교체.
export const CLAIM_TYPES = ['이물', '복장 불량', '위생 불량', '변질·이취', '기타']

export type ClaimGrade = 'A' | 'B' | 'C' | 'D'
export const CLAIM_GRADES: ClaimGrade[] = ['A', 'B', 'C', 'D']
export const CLAIM_GRADE_HINT =
  'A·B 등급은 공유부서에 팝업 알림으로 즉시 전달되고, C·D 등급은 일반 조회됩니다.'
export const isUrgent = (grade: ClaimGrade) => grade === 'A' || grade === 'B'
export const CLAIM_GRADE_TONE: Record<ClaimGrade, TagTone> = {
  A: 'bad',
  B: 'warn',
  C: 'neutral',
  D: 'muted',
}

/** 지금 머물러 있는 단계. 등록 즉시 원인조사 → 원인조사 보고서 제출 시 개선 중 → 개선 보고서 제출 시 완료 */
export type ClaimStatus = '원인조사' | '개선 중' | '완료'
export const CLAIM_STATUSES: ClaimStatus[] = ['원인조사', '개선 중', '완료']
export const CLAIM_STATUS_TONE: Record<ClaimStatus, TagTone> = {
  원인조사: 'bad',
  '개선 중': 'warn',
  완료: 'muted',
}

export type ClaimReport = { content: string; photos: string[]; at: string; by: string }

export type Claim = {
  id: string
  kind: ClaimKind
  storeId: string
  type: string
  grade: ClaimGrade
  /** 매장 이물: 메뉴명 / 원·부자재 이물: 품목명 */
  product: string
  content: string
  /** 점주 1차 대응 내용 — 1차 대응 실패 건만 SV 가 등록한다 */
  firstResponse: string
  photos: string[]
  receivedAt: string // YYYY-MM-DD HH:mm
  receiver: string
  status: ClaimStatus
  /** A·B 등급 공유부서 확인 일시 */
  checkedAt?: string
  /** 원인조사 보고서 */
  cause?: ClaimReport
  /** 매장 개선 보고서 (원·부자재 이물은 업체 개선대책) */
  improve?: ClaimReport
}

export const improveLabel = (kind: ClaimKind) =>
  kind === '매장 이물' ? '매장 개선 보고서' : '업체 개선대책 보고서'

export const fmtDateTime = (value: string) => dayjs(value).format('MM.DD HH:mm')

const ago = (days: number, time = '10:30') =>
  `${dayjs().subtract(days, 'day').format('YYYY-MM-DD')} ${time}`
const base = { photos: [], receiver: '홍길동 SV' }

export const seedClaims: Claim[] = [
  {
    ...base,
    id: 'CL-006',
    kind: '매장 이물',
    storeId: 'GN-A',
    type: '이물',
    grade: 'A',
    product: '아이스 아메리카노',
    content: '음료에서 비닐 조각이 나왔다는 고객 클레임',
    firstResponse: '음료 재제조 제공했으나 고객이 본사 확인 요청',
    receivedAt: ago(0, '09:40'),
    status: '원인조사',
  },
  {
    ...base,
    id: 'CL-005',
    kind: '매장 이물',
    storeId: 'YS-B',
    type: '복장 불량',
    grade: 'C',
    product: '-',
    content: '근무자 위생모 미착용 상태로 제조했다는 고객 제보',
    firstResponse: '점주 사과 및 근무자 교육, 고객 추가 민원 제기',
    receivedAt: ago(1),
    status: '원인조사',
  },
  {
    ...base,
    id: 'CL-004',
    kind: '원·부자재 이물',
    storeId: 'NH-D',
    type: '이물',
    grade: 'B',
    product: '바닐라 시럽 1L',
    content: '시럽 개봉 시 병 입구에서 검은 이물 발견',
    firstResponse: '해당 로트 사용 중지, 동일 로트 재고 보관',
    receivedAt: ago(3),
    status: '원인조사',
    checkedAt: ago(3, '11:00'),
  },
  {
    ...base,
    id: 'CL-003',
    kind: '매장 이물',
    storeId: 'SS-E',
    type: '위생 불량',
    grade: 'B',
    product: '카페라떼',
    content: '컵 외부에 이물이 묻어 있었다는 고객 클레임',
    firstResponse: '음료 교환, 고객 본사 접수 요청',
    receivedAt: ago(5),
    status: '개선 중',
    checkedAt: ago(5, '11:20'),
    cause: {
      content: '컵 보관대 상단 청소 누락. 오픈 전 점검 체크리스트에 보관대 항목 없음.',
      photos: [],
      at: ago(4, '15:00'),
      by: '홍길동 SV',
    },
  },
  {
    ...base,
    id: 'CL-002',
    kind: '매장 이물',
    storeId: 'BS-F',
    type: '변질·이취',
    grade: 'C',
    product: '딸기 라떼',
    content: '우유 맛이 상한 것 같다는 고객 의견',
    firstResponse: '음료 환불 처리',
    receivedAt: ago(9),
    status: '완료',
    cause: {
      content: '우유 개봉 일자 미표기로 개봉 후 3일 경과 제품 사용',
      photos: [],
      at: ago(8, '14:00'),
      by: '홍길동 SV',
    },
    improve: {
      content: '개봉 일자 라벨 부착 의무화, 근무자 재교육 완료',
      photos: [],
      at: ago(6, '17:30'),
      by: '홍길동 SV',
    },
  },
]
